import React, { PureComponent } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { translate } from '@fiverr-private/i18n-react';
import { Switch } from '@fiverr-private/fit';
import { FILTERS_LOCATION } from '@fiverr-private/listing_lib';
import { Container } from '@fiverr-private/layout_components';
import { FilterContext } from '../../../../../../context/filterContext';
import { TOP_GROUPING } from '../../../../../../../config/filters';
import { createBqEventData } from '../../impressionEnrichment/filterShown';
import { hasSupply } from '../utils';
import ToggleTooltip from './ToggleTooltip';

import './index.scss';

const Toggle = (props) => {
    const { title: baseTitle, text, params, tooltip, disabled, ...rest } = props;

    const enrichment = createBqEventData({
        filterId: props.id,
        position: props.position || FILTERS_LOCATION.FLOATING_TOP_RIGHT,
    });
    const [ref] = useImpressionItem({ enrichment });

    if (!hasSupply(props.options)) {
        return null;
    }

    const title = translate(baseTitle, params);

    if (!tooltip) {
        return <ToggleSwitch title={title} innerRef={ref} disabled={disabled} {...rest} />;
    }

    return (
        <ToggleTooltip content={text && translate(text, params)}>
            <ToggleSwitch title={title} innerRef={ref} disabled={disabled} {...rest} />
        </ToggleTooltip>
    );
};

Toggle.displayName = 'Toggle';

class ToggleSwitch extends PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            toggled: props.options && props.options[0].selected,
        };

        this.innerRef = props.innerRef;

        this.handleOnClick = this.handleOnClick.bind(this);
    }

    static contextType = FilterContext;

    handleOnClick = () => {
        const { selectedValue, position, id, onCustomChange, source, disabled } = this.props;

        if (disabled) {
            return;
        }

        const newValue = { [id]: !this.state.toggled ? selectedValue : undefined };

        this.setState((prevState) => ({ ...prevState, toggled: !this.state.toggled }));

        if (onCustomChange) {
            onCustomChange(newValue);
            return;
        }

        const { navigationCondition, applyNavigation, activeFilters } = this.context;

        if (navigationCondition(newValue, activeFilters)) {
            applyNavigation(newValue, activeFilters, TOP_GROUPING.TOGGLERS, position, source);
        }
    };

    render() {
        const { id, options, className, title, badge, disabled } = this.props;
        const { selected } = options[0];

        const toggleContent = (
            <div ref={this.innerRef} className={classNames('toggler-wrapper', className)} onClick={this.handleOnClick}>
                <Switch
                    size={Switch.SIZES.SMALL}
                    id={id}
                    checked={this.state.toggled}
                    value={selected}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled}
                />
                <span>{title}</span>
                {badge && <Container paddingRight="2">{badge}</Container>}
            </div>
        );

        return disabled ? (
            <Container opacity={0.5} pointerEvents="none" cursor="default" userSelect="none">
                {toggleContent}
            </Container>
        ) : (
            toggleContent
        );
    }
}

const ToggleSwitchPropTypes = {
    id: PropTypes.string,
    title: PropTypes.string,
    options: PropTypes.array,
    className: PropTypes.string,
    source: PropTypes.string,
    selectedValue: PropTypes.string,
    badge: PropTypes.element,
    disabled: PropTypes.bool,
};

export const TogglePropTypes = {
    ...ToggleSwitchPropTypes,
    educationContent: PropTypes.string,
    text: PropTypes.string,
    showNotification: PropTypes.bool,
    position: PropTypes.string,
    tooltip: PropTypes.bool,
    onCustomChange: PropTypes.func,
};

Toggle.propTypes = TogglePropTypes;
ToggleSwitch.propTypes = {
    ...ToggleSwitchPropTypes,
    innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

Toggle.defaultProps = {
    checked: false,
    position: FILTERS_LOCATION.FLOATING_TOP,
    tooltip: false,
};

export default Toggle;
